import React, {useEffect} from 'react';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {useQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import type {ApiResponseGetUbiThirdPartyTermsResponse} from '@di-shared/api/di/__generated';
import ImageSelector from '@di-shared/features/common/ImageSelector';

const SAFE_DRIVING_SERVICE_ID = '00000000-0000-0000-0000-000000000000';
const ENCOURAGE_CLOSE_DUE_DATE = 'ENCOURAGE_CLOSE_DUE_DATE';
const ENCOURAGE_DAYS = 30;

export interface SafeDrivingAgreeBannerKrProps {
  vin: string;
}

function SafeDrivingAgreeBannerKr({vin}: SafeDrivingAgreeBannerKrProps) {
  const {t} = useInferableTranslation();
  const [{data: agreementData, isLoading, isError, refetch}, {data: termsData}] = useQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.ubiThirdPartyAgreementsController.getAgreementsForServiceDevelop1), vin],
        queryFn: async () => {
          const response = await diApi.ubiThirdPartyAgreementsController.getAgreementsForServiceDevelop1({vin});
          if (!response) {
            return null;
          }
          return response;
        },
      },
      {
        queryKey: [getQueryKey(diApi.ubiThirdPartyTermsController.getAgreements), vin, SAFE_DRIVING_SERVICE_ID],
        queryFn: () => diApi.ubiThirdPartyTermsController.getAgreements({vin, serviceId: SAFE_DRIVING_SERVICE_ID}),
        select: ({data}: ApiResponseGetUbiThirdPartyTermsResponse) => data,
      },
    ],
  });

  const isAgreement = !!agreementData && agreementData.data.selRcvAgreYn === '1';

  async function openAgreeBottomSheet() {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_AGREE.getFullPath(), {
      vin,
      termsData,
    });
    if (result) {
      await refetch();
    }
  }

  async function openWithdrawBottomSheet() {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_WITHDRAW.getFullPath(), {
      vin,
    });
    if (result) {
      localStorage.setItem(ENCOURAGE_CLOSE_DUE_DATE, new Date().toString());
      await refetch();
    }
  }

  async function openEncourageBottomSheet() {
    // 30일에 한번씩 고도화 독려 바텀시트 노출
    const lastModalTime = localStorage.getItem(ENCOURAGE_CLOSE_DUE_DATE);
    if (isAgreement || isLoading) {
      return;
    }
    if (!lastModalTime) {
      await openAgreeBottomSheet();
      localStorage.setItem(ENCOURAGE_CLOSE_DUE_DATE, new Date().toString());
      return;
    }
    const diffInDays = Math.floor((new Date().getTime() - new Date(lastModalTime).getTime()) / (1000 * 60 * 60 * 24));
    if (diffInDays >= ENCOURAGE_DAYS) {
      await openAgreeBottomSheet();
      localStorage.setItem(ENCOURAGE_CLOSE_DUE_DATE, new Date().toString());
      return;
    }
  }

  useEffect(() => {
    // 2. 고도화 동의 독려 바텀시트 case
    openEncourageBottomSheet();
  }, [agreementData]);

  if (isLoading) {
    return (
      <div className="driving_banner type_square skeleton">
        <button
          type="button"
          className="driving_banner_box"
        >
          <span className="driving_banner_txtbox">
            <strong className="driving_banner_title body_large_b">{t('T20147')}</strong>
            <span
              className="driving_banner_desc body_small_r"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20148')}
            </span>
          </span>
          <span className="driving_banner_imgbox">
            <ImageSelector
              className="driving_banner_img"
              alt="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_improve_service_light.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_improve_service.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service_light.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service_light.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service.png')}
            />
          </span>
        </button>
      </div>
    );
  }

  if (isError) {
    return <></>;
  }

  return (
    <div className="driving_banner type_square">
      <button
        type="button"
        className="driving_banner_box"
        onClick={() => {
          isAgreement ? openWithdrawBottomSheet() : openAgreeBottomSheet();
        }}
      >
        <span className="driving_banner_txtbox">
          <strong className="driving_banner_title body_large_b">{isAgreement ? t('T20203') : t('T20147')}</strong>
          <span
            className="driving_banner_desc body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {isAgreement ? t('T20204') : t('T20148')}
          </span>
        </span>
        <span className="driving_banner_imgbox">
          <ImageSelector
            className="driving_banner_img"
            alt="driving_banner_img"
            kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_improve_service_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_improve_service.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service.png')}
          />
        </span>
      </button>
    </div>
  );
}

export default SafeDrivingAgreeBannerKr;
